import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  Checkbox,
  Grid,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
  Box,
  Tooltip,
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  PermissionAction,
  StudentFilter as StudentFilterModel,
  StudentServiceFilter as StudentServiceFilterModel,
  StudentServiceListDto,
  StudentServiceListFilter,
  StudentServiceListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { StudentFilter } from "pages/students/students/StudentFilter";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { StudentServiceFilter } from "../StudentServiceFilter";
import { studentServicePageRoute } from "../StudentServicePage/StudentServicePage";
import PlaylistAddCheckCircleRoundedIcon from '@mui/icons-material/PlaylistAddCheckCircleRounded';
import { StudentServiceTable } from "./StudentServiceTable";
import { FilterDrawerButton } from "components/common/FilterDrawerButton";
import { FilterDrawer } from "pages/students/students/FilterDrawer";

const routeName = "studentServices";

export const studentServiceListPageRoute: PageRoute = {
  handle: "Services",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StudentServiceListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentService;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentServiceListFilter>();

  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const displayedStartDate = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(firstDayOfMonth);

  const [initListFilter] = useState<StudentServiceListFilter>({
    sortObjects: [],
    pageSize: 20,
    serviceFilter: {
      dateFrom: firstDayOfMonth
    }
  });

  const [listItems, setListItems] = useState<StudentServiceListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentServiceListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const properties = resource.properties || [];

  const [filterOpen, setFilterOpen] = useState(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentServices.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentServices.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    ApiService.studentServices.initListFilter().then((response) => {
      setListFilter(response);
      loadList(response);
    });

    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.studentServices.getListResource().then((response) => {
      setResource(response);
    });
  };

  // const listFilterNode = useListFilter<StudentServiceListFilter>({
  //   maxWidth: "xl",
  //   listFilter: listFilter,
  //   initListFilter: initListFilter,
  //   onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
  //   inputNode: (listFilter, setListFilter) => (
  //     <Stack spacing="lg">
  //       <Stack spacing="lg">
  //         <Typography variant="h6" fontWeight={(theme) => theme.typography.fontWeightRegular}>
  //           Student:
  //         </Typography>

  //         <StudentFilter
  //           filter={listFilter.studentFilter || ({} as StudentFilterModel)}
  //           setFilter={(filter) =>
  //             setListFilter(
  //               produce(listFilter, (draft) => {
  //                 draft.studentFilter = filter;
  //               })
  //             )
  //           }
  //         />
  //       </Stack>

  //       <Stack spacing="lg">
  //         <Typography variant="h6" fontWeight={(theme) => theme.typography.fontWeightRegular}>
  //           Service:
  //         </Typography>

  //         <StudentServiceFilter
  //           filter={listFilter.serviceFilter || ({} as StudentServiceFilterModel)}
  //           setFilter={(filter) =>
  //             setListFilter(
  //               produce(listFilter, (draft) => {
  //                 draft.serviceFilter = filter;
  //               })
  //             )
  //           }
  //         />
  //       </Stack>
  //     </Stack>
  //   ),
  // });

  const getIdsMethods = () => ApiService.studentServices.getIds(listFilter);

  const updateListFilter = (updatedFilter: StudentServiceListFilter) => {
    setListFilter(updatedFilter);
    loadList(updatedFilter, { resetPage: true });
  };

  const groupByDateField = () => {
    const propertyName = pn<StudentServiceFilterModel>("groupByDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Checkbox
        label="Group By Date"
        checked={listFilter.serviceFilter?.groupByDate}
        onChange={(e) => {
          const updatedFilter = produce(listFilter, (draft) => {
            draft.serviceFilter!.groupByDate = e.target.checked;
          });

          setListFilter(updatedFilter);
          loadList(updatedFilter);
        }}
      />
    );
  };

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{studentServiceListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    navigate(studentServicePageRoute.getPath({ id: 0, crudMode: "edit", screenType: "standard" }))
                  }
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <Typography variant="body2" color="primary.main">
          This page displays services from {displayedStartDate} onward.
        </Typography>

        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <Grid container spacing="sm" alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <Box sx={{
                    width: '180px',
                    overflow: 'hidden',
                    '& > *': { maxWidth: '100%' }
                  }}>
                    <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  {groupByDateField()}
                </Grid>
              </Grid>

              <FilterDrawerButton
                initListFilter={initListFilter}
                listFilter={listFilter}
                setFilterOpen={setFilterOpen}
                clearFilter={updateListFilter}
              />
            </Stack>
          }
          tableContainer={
            <StudentServiceTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              studentServices={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      {/* FILTER DRAWER */}
      <FilterDrawer
        listFilter={listFilter}
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onReset={() => {
          setListFilter(initListFilter);
          loadList(initListFilter, { resetPage: true });
        }}
        onApply={() => {
          setListFilter(
            produce(listFilter, (draft) => {
              draft.page = 1;
            })

          );

          loadList(
            produce(listFilter, (draft) => {
              draft.page = 1;
            }),
            { resetPage: true }
          );

          setFilterOpen(false);
        }}
      >
        <StudentServiceFilter
          customFilters={listFilter.customFilters || []}
          setCustomFilters={(filter) =>
            setListFilter(
              produce(listFilter, (draft) => {
                draft.customFilters = filter;
              })
            )
          }
          studentServicefilter={listFilter.serviceFilter || ({} as StudentServiceFilterModel)}
          studentDemographicsFilter={listFilter.studentFilter || {}}
          setStudentServiceFilter={(filter) =>
            setListFilter(
              produce(listFilter, (draft) => {
                draft.serviceFilter = filter;
              })
            )
          }
          setStudentDemographicFilter={(filter) =>
            setListFilter(
              produce(listFilter, (draft) => {
                draft.studentFilter = filter;
              })
            )
          }
        />
      </FilterDrawer>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
